import { EnvelopeIcon } from "@heroicons/react/24/solid";
import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import { z } from "zod";

import { ProfileBadges } from "@/components/profiles/profile-badges";
import { useProfile } from "@/hooks";
import { Breadcrumbs, EmptyState, icons, ScreenContent, ScreenHeader, TabNavigation } from "@/ui";

const ProfileDetailLayout = () => {
  const { id } = Route.useParams();

  const { data: profile, isLoading } = useProfile().getProfile(id);

  if (isLoading) {
    return (
      <div aria-live="polite" className="flex h-105 items-center justify-center" role="status">
        <icons.SpinnerIcon />

        <span className="sr-only">Loading patient…</span>
      </div>
    );
  }

  if (!profile) {
    return (
      <>
        <ScreenHeader>
          <Breadcrumbs items={[{ label: "Profiles", to: "/profiles" }]} />
        </ScreenHeader>

        <ScreenContent>
          <EmptyState
            description="The patient you are looking for does not exist or could not be loaded."
            title="Patient not found"
          >
            <Link className="text-sm font-semibold text-content-strong" to="/profiles">
              <span aria-hidden="true">&larr;</span> Back to profiles
            </Link>
          </EmptyState>
        </ScreenContent>
      </>
    );
  }

  return (
    <>
      <ScreenHeader>
        <Breadcrumbs
          items={[
            { label: "Profiles", to: "/profiles" },
            {
              label: `${profile.firstName} ${profile.lastName}`,
              to: "/profiles/$id",
              params: { id },
            },
          ]}
        />
      </ScreenHeader>

      <ScreenContent>
        <div className="flex gap-2">
          <h2 className="text-2xl font-bold text-content-strong">
            {profile.firstName} {profile.lastName}
          </h2>

          <ProfileBadges profile={profile} />
        </div>

        <div className="flex gap-6 pt-2">
          <div className="flex gap-2 text-content-muted">
            <EnvelopeIcon height={20} />

            <span className="items-center text-sm">{profile.email}</span>
          </div>
        </div>

        <TabNavigation
          tabs={[
            {
              name: "Summary",
              to: "/profiles/$id",
              activeOptions: { exact: true },
              params: { id },
            },
            { name: "Details", to: "/profiles/$id/detail", params: { id } },
            { name: "Careplans", to: "/profiles/$id/careplans", params: { id } },
            { name: "Surveys", to: "/profiles/$id/surveys", params: { id } },
            { name: "Messages", to: "/profiles/$id/messages", params: { id } },
            { name: "Emails", to: "/profiles/$id/emails", params: { id } },
            { name: "SMS", to: "/profiles/$id/sms", params: { id } },
          ]}
        />

        <div className="pb-2">
          <div className="pt-3">
            <Outlet />
          </div>
        </div>
      </ScreenContent>
    </>
  );
};

export const Route = createFileRoute("/_private/profiles/$id")({
  component: ProfileDetailLayout,
  params: {
    parse: (params: Record<string, string>) => {
      return { id: z.number().int().parse(Number(params.id)) };
    },
  },
});
